import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PDFViewer from '../../components/document/PDFViewer';
import { participantService, authService } from '../../services';
import useWebSocket from '../../hooks/useWebSocket';
import useWakeLock from '../../hooks/useWakeLock';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Notes Page Component
 * Split-view page showing workbook on the left and notes editor on the right
 */

const NotesPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const socket = useWebSocket(sessionId);
  // Fix #9: keep screen awake while viewing notes during a live session
  useWakeLock(true);
  const [session, setSession] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showActivityStartedDialog, setShowActivityStartedDialog] = useState(false);
  const [activityInfo, setActivityInfo] = useState(null);
  const [launchedWorkbook, setLaunchedWorkbook] = useState(null);
  const [isTrainerLaunched, setIsTrainerLaunched] = useState(false); // Track if workbook was launched by trainer
  const [allWorkbooks, setAllWorkbooks] = useState([]); // All session workbooks for list view
  const [showWorkbookList, setShowWorkbookList] = useState(true); // Toggle between list and viewer
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [sessionEndReason, setSessionEndReason] = useState('');
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakInfo, setBreakInfo] = useState({ name: '', breakTime: 0 });
  const [showSubsessionStartedModal, setShowSubsessionStartedModal] = useState(false);
  const [subsessionInfo, setSubsessionInfo] = useState({ name: '', number: 0 });
  // Mobile view toggle: 'pdf' shows workbook, 'notes' shows notes editor
  const [mobileView, setMobileView] = useState('pdf');
  // Desktop/Laptop view toggle: 'split' shows both, 'workbook' shows only workbook, 'notes' shows only notes
  const [desktopView, setDesktopView] = useState('split');

  // Navigation items for sidebar
  // Check if session is completed to show additional menu items
  const isSessionCompleted = session?.status === 'completed' || session?.status === 'ended';

  // Check if it's a multi-day session (different start and end dates)
  const isMultiDaySession = session?.startTime && session?.endTime &&
    new Date(session.startTime).toISOString().slice(0, 10) !== new Date(session.endTime).toISOString().slice(0, 10);

  // Show report/content items when session is completed OR when paused (for multi-day sessions)
  const showReportAndContent = isSessionCompleted || (session?.status === 'paused' && isMultiDaySession);

  // Base navigation items (always visible)
  const baseNavigationItems = [
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/active?view=welcome`),
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/active`),
      badge: session?.status === 'live' ? 'Live' : null,
    },
    {
      id: 'workspace',
      name: 'My Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Items only shown after session is completed
  const completedSessionItems = [
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/workbooks`),
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/reports`),
    },
  ];

  // Combine navigation items based on session status
  // Show content and reports when session is completed OR paused (for multi-day sessions)
  const navigationItems = showReportAndContent
    ? [...baseNavigationItems, ...completedSessionItems]
    : baseNavigationItems;

  const bottomNavItems = [
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // Track if initial load is complete
  const initialLoadDone = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Load session and saved notes from API
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load session details
        const sessionResponse = await participantService.getSession(sessionId);
        console.log('[Notes] Session API response:', JSON.stringify(sessionResponse?.data?.launchedWorkbook));

        if (sessionResponse.success && sessionResponse.data) {
          setSession(sessionResponse.data);

          // Check for currently launched workbook from session data
          const sessionData = sessionResponse.data;
          let foundWorkbook = null;

          // Priority 1: Check launchedWorkbook field from API (persisted by backend)
          // Relaxed check: just need name or url to be present
          let wasTrainerLaunched = false;
          if (sessionData.launchedWorkbook && (sessionData.launchedWorkbook.url || sessionData.launchedWorkbook.name)) {
            console.log('[Notes] Found launched workbook from API:', sessionData.launchedWorkbook);
            foundWorkbook = sessionData.launchedWorkbook;
            wasTrainerLaunched = true;
          }

          // Priority 2: Check localStorage (persisted by socket handler)
          if (!foundWorkbook) {
            try {
              const storedWorkbook = localStorage.getItem(`session-${sessionId}-launchedWorkbook`);
              console.log('[Notes] localStorage check:', storedWorkbook ? 'found' : 'not found');
              if (storedWorkbook && storedWorkbook !== 'null' && storedWorkbook !== 'undefined') {
                const parsed = JSON.parse(storedWorkbook);
                if (parsed && (parsed.url || parsed.name)) {
                  foundWorkbook = parsed;
                  wasTrainerLaunched = true; // localStorage stores trainer-launched workbooks
                  console.log('[Notes] Found launched workbook from localStorage:', foundWorkbook);
                }
              }
            } catch (e) {
              console.error('[Notes] Error parsing stored workbook:', e);
            }
          }

          // Priority 3: Fallback - check workbooks array for any that might be marked as launched
          if (!foundWorkbook) {
            const allWorkbooks = sessionData.workbooks || sessionData.subSessions?.flatMap(ss => ss.workbooks || []) || [];
            console.log('[Notes] Checking workbooks array, count:', allWorkbooks.length);
            const currentlyLaunched = allWorkbooks.find(wb =>
              wb.isLaunched || wb.launched || wb.launchedAt
            );

            if (currentlyLaunched) {
              console.log('[Notes] Found launched workbook in array:', currentlyLaunched);
              foundWorkbook = currentlyLaunched;
              wasTrainerLaunched = true; // This workbook was marked as launched
            }
          }

          // Note: We removed the auto-select fallback (Priority 4) because
          // workbooks should only be shown when trainer explicitly launches them

          // Set the found workbook (only if trainer launched)
          if (foundWorkbook) {
            console.log('[Notes] Setting workbook:', foundWorkbook.name || foundWorkbook.url, 'trainerLaunched:', wasTrainerLaunched);
            setLaunchedWorkbook(foundWorkbook);
            setSelectedDocument(foundWorkbook);
            setIsTrainerLaunched(wasTrainerLaunched);
            setShowWorkbookList(false); // Show viewer when workbook is launched
          } else {
            console.log('[Notes] No workbook found from any source');
            setIsTrainerLaunched(false);
            setShowWorkbookList(true); // Show list when no workbook launched
          }

          // Load all workbooks for list view
          const sessionWorkbooks = sessionData.workbooks || [];
          if (sessionWorkbooks.length > 0) {
            const formattedWorkbooks = sessionWorkbooks.map((wb, index) => ({
              id: wb._id || wb.id || `workbook-${index}`,
              _id: wb._id || wb.id || `workbook-${index}`,
              name: wb.name || 'Untitled Workbook',
              url: wb.url,
              type: wb.type,
              size: wb.size || 0,
              uploadedAt: wb.uploadedAt,
              hasValidUrl: wb.hasValidUrl !== undefined ? wb.hasValidUrl : (wb.url && typeof wb.url === 'string' && wb.url.startsWith('http')),
              isCurrentlyLaunched: foundWorkbook && (
                (foundWorkbook.id || foundWorkbook._id) === (wb._id || wb.id) ||
                foundWorkbook.name === wb.name
              ),
            }));
            setAllWorkbooks(formattedWorkbooks);
          }
        }

        // Load saved notes from API
        const notesResponse = await participantService.getNotes(sessionId);
        if (notesResponse.success && notesResponse.data?.content) {
          setNotes(notesResponse.data.content);
        }

        initialLoadDone.current = true;
      } catch (error) {
        console.error('Error loading session data:', error);
        // Fallback to localStorage for notes
        const savedNotes = localStorage.getItem(`session-${sessionId}-notes`);
        if (savedNotes) {
          setNotes(savedNotes);
        }
        initialLoadDone.current = true;
      }
    };

    loadData();
  }, [sessionId]);

  // Auto-save notes to API (debounced)
  const saveNotesToAPI = useCallback(async (notesContent) => {
    try {
      await participantService.saveNotes(sessionId, notesContent);
      // Also save to localStorage as backup
      localStorage.setItem(`session-${sessionId}-notes`, notesContent);
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      // Fallback to localStorage if API fails
      localStorage.setItem(`session-${sessionId}-notes`, notesContent);
      setIsSaving(false);
    }
  }, [sessionId]);

  useEffect(() => {
    // Only auto-save after initial load is complete and notes have changed
    if (!initialLoadDone.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (notes) {
      saveTimeoutRef.current = setTimeout(() => {
        saveNotesToAPI(notes);
      }, 1500); // Debounce 1.5 seconds
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [notes, saveNotesToAPI]);

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    setIsSaving(true);
  };

  const handleClearNotes = () => {
    setShowClearConfirm(true);
  };

  const confirmClearNotes = async () => {
    setNotes('');
    localStorage.removeItem(`session-${sessionId}-notes`);
    // Also clear from API
    try {
      await participantService.saveNotes(sessionId, '');
    } catch (error) {
      console.error('Error clearing notes from API:', error);
    }
  };

  const handleDownloadNotes = () => {
    if (!notes.trim()) return;

    // Create a blob with the notes content
    const blob = new Blob([notes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    // Create a download link
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().slice(0, 10);
    link.download = `${session.name || 'Session'}-Notes-${timestamp}.txt`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Listen for activity and workbook launched events
  useEffect(() => {
    // Use the hook's on/off methods for proper event registration
    if (!socket?.on || !sessionId) return;

    console.log('[Notes] Setting up socket listeners, connected:', socket.isConnected);

    const handleActivityLaunched = (data) => {
      console.log('[Notes] Activity launched:', data);
      setActivityInfo(data);
      setShowActivityStartedDialog(true);
    };

    const handleWorkbookLaunched = (workbookData) => {
      console.log('[Notes] Workbook launched event received:', workbookData);
      // Handle both formats: direct workbook object or wrapped in data.workbook
      const workbook = workbookData.workbook || workbookData;
      if (workbook && (workbook.url || workbook.name)) {
        console.log('[Notes] Setting launched workbook:', workbook.name || workbook.url);
        setLaunchedWorkbook(workbook);
        // Automatically show the launched workbook
        setSelectedDocument(workbook);
        setIsTrainerLaunched(true); // Trainer launched this workbook
        // Store in localStorage for persistence across navigation
        localStorage.setItem(`session-${sessionId}-launchedWorkbook`, JSON.stringify(workbook));
      } else {
        console.warn('[Notes] Received workbook:launched but workbook data is invalid:', workbook);
      }
    };

    const handleWorkbookStopped = (data) => {
      console.log('[Notes] Workbook stopped:', data);
      setLaunchedWorkbook(null);
      setSelectedDocument(null);
      setIsTrainerLaunched(false);
      // Clear from localStorage
      localStorage.removeItem(`session-${sessionId}-launchedWorkbook`);
    };

    const handleSessionEnded = (data) => {
      console.log('[Notes] Session ended:', data);
      setSessionEndReason(data?.reason || 'The trainer has ended this session');
      setShowSessionEndedModal(true);
    };

    const handleSessionStatusChanged = (data) => {
      console.log('[Notes] Session status changed:', data);
      if (data.status === 'completed' || data.status === 'ended') {
        handleSessionEnded(data);
      }
    };

    // Subsession ended - show break modal if there's a break
    const handleSubsessionEnded = (data) => {
      console.log('[Notes] Subsession ended:', data);
      if (data?.hasBreak && data?.breakTime > 0) {
        setBreakInfo({
          name: data.name || 'Current Section',
          breakTime: data.breakTime,
        });
        setShowBreakModal(true);
      }
    };

    // Subsession started - show notification
    const handleSubsessionStarted = (data) => {
      console.log('[Notes] Subsession started:', data);
      // Close break modal if open
      setShowBreakModal(false);
      setSubsessionInfo({
        name: data?.name || 'Next Section',
        number: data?.number || 0,
      });
      setShowSubsessionStartedModal(true);
    };

    socket.on('activity:launched', handleActivityLaunched);
    socket.on('workbook:launched', handleWorkbookLaunched);
    socket.on('workbook:stopped', handleWorkbookStopped);
    socket.on('session:ended', handleSessionEnded);
    socket.on('session-status-changed', handleSessionStatusChanged);
    socket.on('subsession:ended', handleSubsessionEnded);
    socket.on('subsession:started', handleSubsessionStarted);

    // Also check localStorage on connection in case we missed the event during navigation
    if (socket.isConnected) {
      const storedWorkbook = localStorage.getItem(`session-${sessionId}-launchedWorkbook`);
      if (storedWorkbook && storedWorkbook !== 'null' && storedWorkbook !== 'undefined') {
        try {
          const parsed = JSON.parse(storedWorkbook);
          if (parsed && (parsed.url || parsed.name)) {
            console.log('[Notes] Found workbook in localStorage on socket connect:', parsed.name);
            // Only set if not already set (avoid overwriting API data)
            setLaunchedWorkbook(prev => prev || parsed);
            setSelectedDocument(prev => prev || parsed);
            setIsTrainerLaunched(true);
          }
        } catch (e) {
          console.error('[Notes] Error parsing stored workbook on connect:', e);
        }
      }
    }

    return () => {
      socket.off('activity:launched', handleActivityLaunched);
      socket.off('workbook:launched', handleWorkbookLaunched);
      socket.off('workbook:stopped', handleWorkbookStopped);
      socket.off('session:ended', handleSessionEnded);
      socket.off('session-status-changed', handleSessionStatusChanged);
      socket.off('subsession:ended', handleSubsessionEnded);
      socket.off('subsession:started', handleSubsessionStarted);
    };
  }, [socket, sessionId, socket?.isConnected]);

  const handleGoToActivity = () => {
    setShowActivityStartedDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <Card padding="lg" className="max-w-md">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Session Not Found</h3>
            <Button onClick={() => navigate('/join')}>Back to Join</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-neutral-100 overflow-hidden">
      {/* Sidebar - Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Back Button — returns participant to the lobby's welcome view with phase sidebar */}
        <div className="px-3 pt-2">
          <button
            onClick={() => navigate(`/session/${sessionId}/lobby?view=welcome`)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-all text-sm"
            title={sidebarCollapsed ? 'Back to lobby' : ''}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {!sidebarCollapsed && <span>Back</span>}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    item.id === 'workspace'
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {item.id === 'workspace' && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${item.id === 'workspace' ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Nav Items */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-neutral-700 hover:bg-neutral-100"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-neutral-200 px-4 lg:px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* Mobile: Logo */}
            <div className="flex items-center gap-3 lg:hidden">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <span className="text-lg font-bold text-neutral-900">Aktivity</span>
            </div>

            {/* Desktop: Page Title and View Toggle */}
            <div className="hidden lg:flex items-center gap-4">
              <h1 className="text-lg font-semibold text-neutral-900">My Workspace</h1>
              {/* Desktop View Toggle Buttons */}
              <div className="flex items-center bg-neutral-100 rounded-lg p-1">
                <button
                  onClick={() => setDesktopView('workbook')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    desktopView === 'workbook'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  title="Show Workbook Only"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Workbook
                </button>
                <button
                  onClick={() => setDesktopView('split')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    desktopView === 'split'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  title="Show Both Side by Side"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                  </svg>
                  Split
                </button>
                <button
                  onClick={() => setDesktopView('notes')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    desktopView === 'notes'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                  title="Show Notes Only"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Notes
                </button>
              </div>
            </div>

            {/* Right: Status badges */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {session.status === 'live' ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 rounded-md">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-700">Live</span>
                </div>
              ) : session.status === 'completed' ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 rounded-md">
                  <span className="text-sm font-medium text-neutral-700">Completed</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 rounded-md">
                  <span className="text-sm font-medium text-blue-700">Scheduled</span>
                </div>
              )}
              <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                <span className="text-sm font-bold text-orange-700">#{session.code}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Split View Container */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden pb-16 lg:pb-0 relative">
          {/* Left Side - Workbooks & Viewer */}
          {/* On mobile: show only when mobileView is 'pdf', On desktop: based on desktopView */}
          <div className={`${mobileView === 'pdf' ? 'flex' : 'hidden'} ${desktopView === 'notes' ? 'lg:hidden' : 'lg:flex'} flex-1 lg:flex-none h-full lg:h-full ${desktopView === 'split' ? 'lg:w-1/2' : 'lg:w-full'} border-b lg:border-b-0 ${desktopView !== 'workbook' ? 'lg:border-r' : ''} border-neutral-200 bg-white flex-col min-h-0`}>
            {/* Header with tabs */}
            <div className="p-3 sm:p-4 border-b border-neutral-200 bg-neutral-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Tab buttons */}
                  <button
                    onClick={() => setShowWorkbookList(true)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      showWorkbookList
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      Workbooks
                    </span>
                  </button>
                  <button
                    onClick={() => setShowWorkbookList(false)}
                    disabled={!selectedDocument}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      !showWorkbookList
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Viewer
                    </span>
                  </button>
                </div>
                {selectedDocument && !showWorkbookList && (
                  <span className="text-sm text-neutral-500 truncate max-w-[150px]">{selectedDocument.name}</span>
                )}
              </div>
            </div>

            {/* Currently Launched Workbook Banner */}
            {launchedWorkbook && isTrainerLaunched && (
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-3 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-xs font-semibold text-blue-100 uppercase tracking-wide">Currently Launched by Trainer</p>
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    </div>
                    <h3 className="text-sm font-bold text-white truncate">{launchedWorkbook.name || 'Workbook'}</h3>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedDocument(launchedWorkbook);
                      setShowWorkbookList(false);
                    }}
                    className="px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    View
                  </button>
                </div>
              </div>
            )}


            {/* Content Area - Either Workbook List or Viewer */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {showWorkbookList ? (
                /* Workbook List View */
                <div className="flex-1 overflow-y-auto p-4">
                  {allWorkbooks.length > 0 ? (
                    <div className="space-y-3">
                      {allWorkbooks.map((workbook) => {
                        const getFileIcon = () => {
                          const ext = workbook.name?.split('.').pop()?.toLowerCase();
                          if (ext === 'pdf') {
                            return (
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            );
                          }
                          if (['pptx', 'ppt'].includes(ext)) {
                            return (
                              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            );
                          }
                          if (['docx', 'doc'].includes(ext)) {
                            return (
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            );
                          }
                          return (
                            <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-neutral-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                              </svg>
                            </div>
                          );
                        };

                        return (
                          <div
                            key={workbook.id}
                            className={`bg-white border rounded-xl p-4 cursor-pointer hover:shadow-md transition-all ${
                              workbook.isCurrentlyLaunched ? 'border-blue-300 ring-2 ring-blue-100' : 'border-neutral-200'
                            }`}
                            onClick={() => {
                              if (workbook.hasValidUrl) {
                                setSelectedDocument(workbook);
                                setShowWorkbookList(false);
                              }
                            }}
                          >
                            <div className="flex items-center gap-4">
                              {getFileIcon()}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-medium text-neutral-900 truncate">{workbook.name}</h3>
                                  {workbook.isCurrentlyLaunched && (
                                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                      Live
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-neutral-500 mt-0.5">
                                  {workbook.type || 'Document'} • {workbook.size ? `${(workbook.size / 1024).toFixed(1)} KB` : 'Size unknown'}
                                </p>
                              </div>
                              <button
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                  workbook.hasValidUrl
                                    ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                                    : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                                }`}
                                disabled={!workbook.hasValidUrl}
                              >
                                {workbook.hasValidUrl ? 'View' : 'Unavailable'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center text-neutral-500 p-4">
                        <svg className="w-12 h-12 mx-auto mb-3 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                        <p className="text-base font-medium">No workbooks available</p>
                        <p className="text-sm mt-1">Workbooks will appear here once uploaded by the trainer</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedDocument ? (
                /* Document Viewer */
                <div className="flex-1 overflow-hidden bg-neutral-800 flex flex-col">
                  {/* Check for valid URL - not blob: and not empty */}
                  {selectedDocument.url && !selectedDocument.url.startsWith('blob:') ? (
                    <>
                      {/* PDF files - use PDFViewer component for all screen sizes */}
                      {selectedDocument.name?.toLowerCase().endsWith('.pdf') ? (
                        <PDFViewer
                          fileUrl={selectedDocument.url}
                          fileName={selectedDocument.name}
                          showThumbnails={false}
                          enableFullscreen={true}
                        />
                      ) : /* Microsoft Office files - use Office Online viewer */
                      /\.(pptx?|docx?|xlsx?)$/i.test(selectedDocument.name) ? (
                        <iframe
                          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selectedDocument.url)}`}
                          title={selectedDocument.name}
                          className="w-full h-full border-0"
                          allow="fullscreen"
                        />
                      ) : (
                        /* Other files - use direct iframe */
                        <iframe
                          src={selectedDocument.url}
                          title={selectedDocument.name}
                          className="w-full h-full border-0"
                          allow="fullscreen"
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full p-8">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 bg-neutral-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                          <svg className="w-8 h-8 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-white mb-2">Workbook Unavailable</h4>
                        <p className="text-neutral-400 mb-4">
                          This workbook needs to be re-uploaded by the trainer to be viewable.
                        </p>
                        <button
                          onClick={() => setShowWorkbookList(true)}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Back to Workbooks
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-neutral-500 p-4">
                    <svg className="w-12 h-12 mx-auto mb-3 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <p className="text-base font-medium">No workbook selected</p>
                    <p className="text-sm mt-1">Select a workbook from the list to view it here</p>
                    <button
                      onClick={() => setShowWorkbookList(true)}
                      className="mt-4 px-4 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium hover:bg-primary-200 transition-colors"
                    >
                      View Workbooks
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Notes Editor (Black Card) */}
          {/* On mobile: show only when mobileView is 'notes', On desktop: based on desktopView */}
          <div className={`${mobileView === 'notes' ? 'flex' : 'hidden'} ${desktopView === 'workbook' ? 'lg:hidden' : 'lg:flex'} flex-1 lg:flex-none h-full lg:h-full ${desktopView === 'split' ? 'lg:w-1/2' : 'lg:w-full'} bg-neutral-900 flex-col min-h-0`}>
            <div className="p-3 sm:p-4 border-b border-neutral-700 bg-neutral-800 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-neutral-700 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white">My Notes</h2>
                  <p className="text-xs text-neutral-400 hidden sm:block">Auto-saved locally</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-400 hidden sm:inline">
                  {notes.length} chars
                </span>
                <button
                  onClick={handleDownloadNotes}
                  disabled={!notes.trim()}
                  className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  title="Download notes as text file"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  onClick={handleClearNotes}
                  className="px-3 py-1.5 bg-neutral-700 hover:bg-neutral-600 text-white text-xs sm:text-sm rounded-md transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Notes Textarea */}
            <div className="flex-1 p-3 sm:p-4">
              <textarea
                value={notes}
                onChange={handleNotesChange}
                placeholder="Start typing your notes here...&#10;&#10;• Key points from the session&#10;• Important concepts&#10;• Questions to ask&#10;• Action items"
                className="w-full h-full bg-neutral-800 text-white placeholder-neutral-500 rounded-lg p-3 sm:p-4 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 border border-neutral-700 font-mono text-xs sm:text-sm leading-relaxed"
              />
            </div>

            {/* Bottom Info Bar */}
            <div className="p-3 sm:p-4 border-t border-neutral-700 bg-neutral-800 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-neutral-400">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Auto-save</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <span className="hidden sm:inline">Lines: {notes.split('\n').length}</span>
                  <span>Words: {notes.trim() ? notes.trim().split(/\s+/).length : 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Toggle Button - Floating button to switch between PDF and Notes */}
          <div className="lg:hidden fixed bottom-20 right-4 z-30">
            <button
              onClick={() => setMobileView(mobileView === 'pdf' ? 'notes' : 'pdf')}
              className="flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg transition-all active:scale-95"
            >
              {mobileView === 'pdf' ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="font-medium">Notes</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span className="font-medium">Workbook</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                item.id === 'workspace' ? '' : 'text-neutral-500'
              }`}
              style={item.id === 'workspace' ? { color: '#ff8a00' } : {}}
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Clear Notes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={confirmClearNotes}
        title="Clear All Notes?"
        message="Are you sure you want to clear all notes? This action cannot be undone."
        confirmText="OK"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Activity Started Dialog */}
      <ConfirmDialog
        isOpen={showActivityStartedDialog}
        onClose={() => setShowActivityStartedDialog(false)}
        onConfirm={handleGoToActivity}
        title={`${activityInfo?.activityType === 'poll' ? 'Poll' : activityInfo?.activityType === 'quiz' ? 'Quiz' : 'Activity'} Started!`}
        message={`${activityInfo?.activityTitle || 'An activity'} has been launched. Go back to activities to participate.`}
        confirmText="Go to Activity"
        cancelText="Stay Here"
        variant="info"
      />

      {/* Session Ended Modal */}
      <Modal
        isOpen={showSessionEndedModal}
        onClose={() => {
          setShowSessionEndedModal(false);
          if (authService.isAuthenticated()) {
            navigate('/participant/dashboard');
          } else {
            navigate('/join');
          }
        }}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-orange-100 mb-5">
            <svg className="h-10 w-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Ended</h3>
          <p className="text-base text-neutral-600 mb-6 leading-relaxed">
            {sessionEndReason || 'The trainer has ended this session. Thank you for participating!'}
          </p>
          {session && (
            <div className="bg-neutral-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-neutral-900">{session.name}</p>
              <p className="text-xs text-neutral-500 mt-1">Session Code: {session.code}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setShowSessionEndedModal(false);
                navigate(`/session/${sessionId}/reports`);
              }}
              fullWidth
              size="lg"
            >
              View My Report
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowSessionEndedModal(false);
                if (authService.isAuthenticated()) {
                  navigate('/participant/dashboard');
                } else {
                  navigate('/join');
                }
              }}
              fullWidth
            >
              {authService.isAuthenticated() ? 'Return to Dashboard' : 'Join Another Session'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Break Time Modal */}
      <Modal
        isOpen={showBreakModal}
        onClose={() => setShowBreakModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          {/* Break Icon */}
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-5">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">
            Time for a Break!
          </h3>

          {/* Section Name */}
          <p className="text-base text-neutral-600 mb-2">
            "{breakInfo.name}" has ended
          </p>

          {/* Break Duration */}
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-blue-600 font-medium mb-2">Break Duration</p>
            <p className="text-4xl font-bold text-blue-700">
              {breakInfo.breakTime} <span className="text-xl font-normal">min</span>
            </p>
          </div>

          {/* Message */}
          <p className="text-sm text-neutral-500 mb-6">
            Take a moment to stretch, grab a drink, or relax. The session will resume shortly.
          </p>

          {/* OK Button */}
          <Button
            variant="primary"
            onClick={() => setShowBreakModal(false)}
            fullWidth
            size="lg"
          >
            Got it!
          </Button>
        </div>
      </Modal>

      {/* Subsession Started Modal (Break Ended) */}
      <Modal
        isOpen={showSubsessionStartedModal}
        onClose={() => setShowSubsessionStartedModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-5">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Resuming!</h3>
          <p className="text-base text-neutral-600 mb-2">
            {subsessionInfo.number > 0 ? `Section ${subsessionInfo.number}` : 'Next section'} is starting
          </p>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-green-600 font-medium mb-2">Now Starting</p>
            <p className="text-xl font-bold text-green-700">"{subsessionInfo.name}"</p>
          </div>
          <p className="text-sm text-neutral-500 mb-6">
            Get ready! New activities may be launched soon.
          </p>
          <Button variant="primary" onClick={() => setShowSubsessionStartedModal(false)} fullWidth size="lg">
            I'm Ready!
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default NotesPage;
